import React from 'react'
import PropTypes from 'prop-types'
import { createGlobalStyle } from 'styled-components'

import josefinLightWoff from 'typeface-josefin-sans/files/josefin-sans-latin-300.woff'
import josefinLightWoff2 from 'typeface-josefin-sans/files/josefin-sans-latin-300.woff2'

import ralewayWoff from 'typeface-raleway/files/raleway-latin-400.woff'
import ralewayWoff2 from 'typeface-raleway/files/raleway-latin-400.woff2'
import Helmet from 'react-helmet'
import * as base from '../styles/base.scss'

import styles from './layout.module.scss'
import Header from '../components/header'
import Footer from '../components/footer'
import NewsletterSignup from '../components/newsletter-signup'
import PageTransition from '../components/page-transition'
import CookieBanner from '../components/cookie-banner'
import SEO from '../components/metadata/seo'
// import { GridTest } from '../components/grid'

const GlobalStyle = createGlobalStyle`
/* josefin-sans-300normal - latin */
@font-face {
  font-family: 'Josefin Sans';
  font-style: normal;
  font-weight: 300;
  src:
    local('Josefin Sans Light'),
    local('Josefin Sans-Light'),
    url('${josefinLightWoff2}') format('woff2'), /* Super Modern Browsers */
    url('${josefinLightWoff}') format('woff'); /* Modern Browsers */
}

/* raleway-300normal - latin */
@font-face {
  font-family: 'Raleway';
  font-style: normal;
  font-weight: 400;
  src:
    local('Raleway Light'),
    local('Raleway-Light'),
    url('${ralewayWoff2}') format('woff2'), /* Super Modern Browsers */
    url('${ralewayWoff}') format('woff'); /* Modern Browsers */
}

${base}
`

export const TranslationContext = React.createContext({})

const Layout = props => {
  const { site, page, children, location, languageMenuItems } = props
  return (
    <TranslationContext.Provider value={site.translations}>
      <GlobalStyle />
      <SEO
        host={site.siteMetadata.siteUrl}
        alternateLanguages={page.alternateLanguages}
        siteTitle={site.title}
        defaultLang={site.siteMetadata.defaultLanguage}
        {...page.seo}
      />
      <div
        className={styles.el}
        data-transparent-header={page.transparentHeader}
      >
        <Header
          headerTitle={site.headerTitle || site.title}
          pageLang={page.lang}
          navItems={site.nav}
          navHeadingItems={site.navHeader}
          path={page.path}
          transparent={page.transparentHeader}
          languageMenuItems={languageMenuItems}
          hideLogo={page.hideLogo}
        />

        <Helmet>
          <link
            rel='preload'
            href={josefinLightWoff2}
            as='font'
            crossOrigin='anonymous'
          />
          <link
            rel='preload'
            href={ralewayWoff2}
            as='font'
            crossOrigin='anonymous'
          />
        </Helmet>

        <main className={styles.el__main}>
          <PageTransition location={location}>
            {children}
            {/* <GridTest /> */}
          </PageTransition>
        </main>
        {page.newsletterSubscribe && site.newsletterActionURL && (
          <NewsletterSignup
            heading={site.newsletterHeading}
            description={site.newsletterDescription}
            submitLabel={site.newsletterSubmitLabel}
            actionURL={site.newsletterActionURL}
            fields={site.newsletterFields}
          />
        )}
        {!page.hideFooter && (
          <Footer
            columns={site.footer}
            notesPrimary={site.acknowledgmentStatement}
            notesSecondary={site.footerNotes}
            socialLinks={site.socialLinks}
          />
        )}
        <CookieBanner cookiePolicy={site.cookiePolicy} />
      </div>
    </TranslationContext.Provider>
  )
}

Layout.defaultProps = {
  site: {
    headerTitle: '',
    metaDescription: '',
    acknowledgmentStatement: '',
    nav: [],
    navHeader: [],
    shareImage: '',
    footer: [],
    footerNotes: null,
    newsletterHeading: '',
    newsletterDescription: '',
    newsletterSubmitLabel: '',
    newsletterActionURL: '',
    newsletterFields: [],
    socialLinks: [],
  },
  page: {
    shareImage: '',
    path: '',
    newsletterSubscribe: false,
    transparentHeader: false,
    darkerBg: false,
    hideLogo: false,
    hideFooter: false,
  },
  location: {},
  languageMenuItems: []
}

Layout.propTypes = {
  site: PropTypes.shape({
    title: PropTypes.string.isRequired,
    headerTitle: PropTypes.string,
    metaDescription: PropTypes.string.isRequired,
    acknowledgmentStatement: PropTypes.string,
    nav: PropTypes.arrayOf(PropTypes.object),
    navHeader: PropTypes.arrayOf(PropTypes.object),
    shareImage: PropTypes.string,
    footer: PropTypes.arrayOf(PropTypes.object),
    footerNotes: PropTypes.shape({
      raw: PropTypes.array.isRequired,
      html: PropTypes.string,
      text: PropTypes.string,
    }),
    newsletterHeading: PropTypes.string,
    newsletterDescription: PropTypes.string,
    newsletterSubmitLabel: PropTypes.string,
    newsletterActionURL: PropTypes.string,
    newsletterFields: PropTypes.arrayOf(PropTypes.object),
    socialLinks: PropTypes.arrayOf(PropTypes.object),
    siteMetadata: PropTypes.shape({
      defaultLanguage: PropTypes.string.isRequired,
      siteUrl: PropTypes.string.isRequired,
    }),
    translations: PropTypes.object,
  }),
  page: PropTypes.shape({
    title: PropTypes.string.isRequired,
    lang: PropTypes.string.isRequired,
    shareImage: PropTypes.string,
    path: PropTypes.string,
    newsletterSubscribe: PropTypes.bool,
    transparentHeader: PropTypes.bool,
    darkerBg: PropTypes.bool,
    hideLogo: PropTypes.bool,
    hideFooter: PropTypes.bool,
    alternateLanguages: PropTypes.array,
  }),
  children: PropTypes.node.isRequired,
  location: PropTypes.object,
  languageMenuItems: PropTypes.array
}

export default Layout
